import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LogOut, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { AbandonDialog, motifLabel } from './AbandonPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? format(parseISO(d.slice(0, 10)), 'dd/MM/yyyy') : '');

export const AbandonsDossierDialog = ({ dossierId, candidatsActifs, nbAbandons, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [declareFor, setDeclareFor] = useState('');
  const [declareOpen, setDeclareOpen] = useState(false);

  const load = () => axios.get(`${API_URL}/dossiers/${dossierId}/abandons`).then(r => setList(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const annuler = async (c) => {
    if (!window.confirm(`Annuler l'abandon de ${c.nom} ${c.prenom} ?`)) return;
    try { await axios.delete(`${API_URL}/dossiers/${dossierId}/candidats/${c.id}/abandon`); toast.success('Abandon annulé'); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const candidatToDeclare = candidatsActifs.find(c => c.id === declareFor);

  return (
    <>
      <Button type="button" variant="outline" size="sm" className={nbAbandons > 0 ? 'text-red-700 border-red-200 hover:bg-red-50' : ''} onClick={() => setOpen(true)} data-testid="abandons-dossier-btn">
        <LogOut className="w-3.5 h-3.5 mr-1" /> Candidats ayant abandonné ({nbAbandons})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="abandons-dossier-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LogOut className="w-5 h-5 text-red-500" /> Abandons sur cette action</DialogTitle></DialogHeader>
          {list.length === 0 ? <p className="text-sm text-slate-400 italic">Aucun abandon déclaré.</p> : (
            <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
              {list.map(c => (
                <div key={c.id} className="flex items-start justify-between gap-3 px-4 py-3" data-testid={`abandon-item-${c.id}`}>
                  <div className="text-sm">
                    <p className="font-medium text-slate-900">{c.nom} {c.prenom}</p>
                    <p className="text-red-700 text-xs">Le {fmt(c.abandon.date)} — {motifLabel(c.abandon.motif)}</p>
                    {c.abandon.commentaire && <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{c.abandon.commentaire}</p>}
                    <p className="text-[11px] text-slate-400 mt-0.5">Déclaré par {c.abandon.declare_par_nom || '—'} le {fmt(c.abandon.declare_le)}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => annuler(c)} data-testid={`abandon-annuler-${c.id}`}><Undo2 className="w-3.5 h-3.5 mr-1" /> Annuler</Button>
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-3 flex items-center gap-2 flex-wrap">
            <p className="text-xs text-slate-500 w-full">Déclarer un nouvel abandon :</p>
            <Select value={declareFor} onValueChange={setDeclareFor}>
              <SelectTrigger className="flex-1 min-w-[220px]" data-testid="abandon-select-candidat"><SelectValue placeholder="Choisir un candidat actif…" /></SelectTrigger>
              <SelectContent>{candidatsActifs.map(c => <SelectItem key={c.id} value={c.id}>{c.nom} {c.prenom}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" size="sm" className="bg-red-600 hover:bg-red-700" disabled={!candidatToDeclare} onClick={() => setDeclareOpen(true)} data-testid="abandon-declarer-dossier-btn">Déclarer l'abandon</Button>
          </div>
          {candidatToDeclare && <AbandonDialog open={declareOpen} onOpenChange={setDeclareOpen} dossierId={dossierId} candidat={candidatToDeclare} onDone={() => { setDeclareFor(''); load(); onChanged?.(); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
